import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  model,
  PLATFORM_ID,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';

import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {isPlatformBrowser} from '@angular/common';

import {Combobox, ComboboxDialog, ComboboxInput, ComboboxPopupContainer,} from '@angular/aria/combobox';

import type {Dayjs} from 'dayjs';
import dayjs from 'dayjs';

import {AriaComboboxDialogPanelDirective} from '../directives/aria-combobox-dialog-panel.directive';
import { GenericService } from '../../../services/generic.service';

import type {
  AriaDatePickerFixedRangePreset,
  AriaDatePickerFormValue,
  AriaDatePickerGranularity,
  AriaDatePickerMobileDrawer,
  AriaDatePickerMode,
  AriaDatePickerModelFormat,
  AriaDatePickerPeriod,
  AriaDatePickerRangeConstraint,
  AriaDatePickerSelection,
} from '../models/aria-date-picker.model';

import {createAriaFieldId} from '../utils/aria-field-id';

import {
  ARIA_DATE_DISPLAY_FORMAT,
  computeDynamicMaxDate,
  computeFixedRangeEnd,
  computePeriodEnd,
  formatDisplayValue,
  formValueToSelection,
  isDateSelectable,
  normalizeByGranularity,
  normalizeRangeSelection,
  selectionToFormValue,
  toDay,
} from '../utils/aria-date-picker.utils';

import {closeAriaCombobox} from '../utils/aria-combobox-close';
import {resolveAriaMobileDrawer} from '../utils/aria-mobile-drawer';
import { AppIconComponent } from '../app-icon/app-icon.component';
import { AriaDatePickerPanelComponent } from '../aria-date-picker-panel/aria-date-picker-panel.component';
import { AriaFieldPrefixDirective } from '../directives/aria-field-prefix.directive';
import { IconColor } from '../app-icon';

/**
 * Date picker custom accessibile per Europ Assistance.
 *
 * **Sostituisce:** `app-datapicker-input`, `app-text-input-datepicker`
 *
 * **Pattern:** trigger `ngCombobox` + pannello calendario `ngGrid` (Angular Aria).
 * Su mobile opzionale drawer (`mobileDrawer`) con `ngComboboxDialog`.
 *
 * ## Modalità (`mode`)
 * | Mode | Comportamento | Valore form (default `iso`) |
 * |------|---------------|-----------------------------|
 * | `single` | Una data | `string` ISO |
 * | `range` | Due click (inizio → fine) | `[string, string]` |
 * | `period` | Un click, fine automatica (`period`) | `[string, string]` |
 * | `fixed-range` | Un click, fine da preset (`fixedRange`) | `[string, string]` |
 *
 * ## Migrazione da `Configuration` legacy
 * | Legacy | Nuovo input |
 * |--------|-------------|
 * | `rangeDate: true` | `mode="range"` |
 * | `doubleFormat` + `semestrale` | `mode="period"` `period="semestrale"` |
 * | `doubleFormat` + `mensile` | `mode="period"` `period="mensile"` |
 * | `doubleFormat` + `settimanale` | `mode="period"` `period="settimanale"` |
 * | `doubleFormat` (default) | `mode="period"` `period="annual"` |
 * | `fixedRange` | `mode="fixed-range"` `fixedRange="3\|15\|annual"` |
 * | `double` | `doubleCalendar` |
 * | `selectYears` / mode years\|months | `granularity` + `enableYearMonthNavigation` |
 * | mobile popup | `mobileDrawer="auto"` |
 * | `minDate` / `maxDate` | `minDate` / `maxDate` |
 * | `maxDays`, `maxDateOneMonth` | `rangeConstraint` |
 *

 * @example
 * ```html
 * <!-- Doppio calendario affiancato (ex double) -->
 * <app-aria-date-picker-field
 *   label="Date viaggio"
 *   mode="range"
 *   [doubleCalendar]="true"
 *   formControlName="tripDates"
 * />
 *
 * <!-- Solo mese/anno -->
 * <app-aria-date-picker-field
 *   label="Mese decorrenza"
 *   granularity="month"
 *   formControlName="startMonth"
 * />
 * ```
 */

@Component({
  selector: 'app-aria-date-picker-field',
  standalone: true,
  imports: [
    Combobox,
    ComboboxDialog,
    ComboboxInput,
    ComboboxPopupContainer,
    AriaComboboxDialogPanelDirective,
    AriaDatePickerPanelComponent,
    AppIconComponent,
  ],
  templateUrl: './aria-date-picker-field.component.html',
  styleUrl: './aria-date-picker-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AriaDatePickerFieldComponent),
      multi: true,
    },
  ],
})

export class AriaDatePickerFieldComponent implements ControlValueAccessor {
  private readonly generic = inject(GenericService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly comboboxRef = viewChild<Combobox<unknown>>('comboboxRef');
  private readonly panelRefs = viewChildren(AriaDatePickerPanelComponent);
  private readonly popupPanel = viewChild<ElementRef<HTMLElement>>('popupPanel');
  protected readonly hasPrefix = contentChild(AriaFieldPrefixDirective);

  readonly label = input<string>('');
  /** `floating`: label dentro il campo (stile legacy preventivatore). `stacked`: label sopra il controllo. */
  readonly labelLayout = input<'stacked' | 'floating'>('floating');
  readonly placeholder = input('Seleziona una data');
  readonly required = input(false, { transform: (v: boolean | string) => v === true || v === '' });
  readonly hint = input<string | null>(null);
  readonly errorMessage = input<string | null>(null);
  readonly name = input<string | undefined>(undefined);

  readonly mode = input<AriaDatePickerMode>('single');
  readonly period = input<AriaDatePickerPeriod>('annual');
  readonly fixedRange = input<AriaDatePickerFixedRangePreset>('annual');
  readonly rangeConstraint = input<AriaDatePickerRangeConstraint>({});
  readonly minDate = input<Date | string | null>(null);
  readonly maxDate = input<Date | string | null>(null);

  /**
   * Giorni minimi da oggi (regole business).
   * Es. `3` → selezionabile solo da oggi + 3 giorni.
   * Se usato insieme a `minDate`, viene applicato il vincolo più restrittivo.
   */
  readonly minDaysFromToday = input<number | null>(null);
  readonly displayFormat = input(ARIA_DATE_DISPLAY_FORMAT);
  readonly modelFormat = input<AriaDatePickerModelFormat>('iso');

  /** Due mesi affiancati (ex `double` legacy). */
  readonly doubleCalendar = input(false);

  /** Drill-down mese/anno nel header (ex `selectYears`). */
  readonly enableYearMonthNavigation = input(true);

  /** Selezione a livello giorno, mese o anno. */
  readonly granularity = input<AriaDatePickerGranularity>('day');

  /** Drawer bottom-sheet su mobile (`auto` = rileva viewport). */
  readonly mobileDrawer = input<AriaDatePickerMobileDrawer>('auto');
  /** Colore icona calendario nel suffix. */
  readonly suffixIconColor = input<IconColor | null>('dark-grey');


  protected readonly fieldId = createAriaFieldId('aria-date-picker');
  protected readonly hintId = `${this.fieldId}-hint`;
  protected readonly errorId = `${this.fieldId}-error`;
  protected readonly displayValue = model('');
  protected readonly isDisabled = signal(false);
  protected readonly popupPlacement = signal<'above' | 'below'>('below');
  protected readonly viewMonth = signal(dayjs().startOf('month'));
  protected readonly selection = signal<AriaDatePickerSelection | null>(null);
  protected readonly rangeDraftStart = signal<Dayjs | null>(null);
  protected readonly dynamicMaxDate = signal<Dayjs | null>(null);
  protected readonly enableRangePreview = computed(
    () => this.mode() === 'range' && this.rangeDraftStart() != null,
  );
  protected readonly useFloatingLabel = computed(
    () => this.labelLayout() === 'floating' && !!this.label(),
  );
  protected readonly isFilled = computed(() => this.displayValue().trim().length > 0);
  private readonly wasExpanded = signal(false);
  private openedOnce = false;

  protected readonly useMobileDrawer = computed(() =>
    resolveAriaMobileDrawer(this.mobileDrawer(), this.generic.isMobile())
  );

  protected readonly minDay = computed(() => {
    let min = toDay(this.minDate());

    const offsetDays = this.minDaysFromToday();
    if (offsetDays != null && offsetDays >= 0) {
      const offsetMin = dayjs().add(offsetDays, 'day').startOf('day');
      min = !min || offsetMin.isAfter(min, 'day') ? offsetMin : min;
    }

    const draft = this.rangeDraftStart();
    if (this.mode() === 'range' && draft) {
      min = !min || draft.isAfter(min, 'day') ? draft : min;
    }

    return min;
  });

  protected readonly maxDay = computed(() => {

    const dynamic = this.dynamicMaxDate();

    const global = toDay(this.maxDate());

    if (dynamic && global) {

      return dynamic.isBefore(global, 'day') ? dynamic : global;

    }

    return dynamic ?? global;

  });



  protected readonly isRangeLike = computed(

    () => this.mode() === 'range' || this.mode() === 'period' || this.mode() === 'fixed-range'

  );

  constructor() {
    // Inizializza il mese al minDate se è nel futuro
    effect(() => {
      const min = toDay(this.minDate());
      if (min && min?.isAfter(dayjs(), 'month')) {
        this.viewMonth.set(min.startOf('month'));
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const combobox = this.comboboxRef();
      const expanded = Boolean(combobox?.expanded());

      if (expanded && !this.wasExpanded()) {
        this.openedOnce = true;
        this.pendingInitialFocus = true;
      }

      // Se l'utente apre e richiude senza selezionare, consideriamo "touched"
      // (il parent deciderà se mostrare errore, es. required).
      if (
        !expanded &&
        this.wasExpanded() &&
        this.openedOnce &&
        this.selection() == null
      ) {
        this.onTouched();
      }

      this.wasExpanded.set(expanded);
    });

    afterRenderEffect(() => {
      const combobox = this.comboboxRef();
      if (!combobox?.expanded()) {
        this.pendingInitialFocus = false;
        return;
      }
      if (this.pendingInitialFocus) {
        this.applyInitialFocus();
      }
    });

    afterRenderEffect(() => {
      const combobox = this.comboboxRef();
      if (this.useMobileDrawer() || !combobox?.expanded()) {
        return;
      }
      this.updatePopupPlacement();
    });

    this.setupClickOutsideToClose();
  }



  private onChange: (value: AriaDatePickerFormValue) => void = () => undefined;

  private onTouched: () => void = () => undefined;

  private syncingFromForm = false;

  private pendingInitialFocus = false;



  protected inputPlaceholder(): string {
    return this.useFloatingLabel() ? '' : this.placeholder();
  }

  protected describedBy(): string | null {

    const ids: string[] = [];

    if (this.hint()) {

      ids.push(this.hintId);

    }

    if (this.errorMessage()) {

      ids.push(this.errorId);

    }

    return ids.length ? ids.join(' ') : null;

  }



  protected onDaySelected(day: Dayjs): void {

    if (this.syncingFromForm) {

      return;

    }



    const normalizedDay = normalizeByGranularity(day, this.granularity());

    if (!isDateSelectable(normalizedDay, this.minDay(), this.maxDay())) {
      return;
    }

    const mode = this.mode();



    if (mode === 'single') {

      this.commitSelection({ start: normalizedDay }, true);

      return;

    }



    if (mode === 'period') {

      const end = computePeriodEnd(normalizedDay, this.period());

      this.commitSelection({ start: normalizedDay, end }, true);

      return;

    }



    if (mode === 'fixed-range') {

      const end = computeFixedRangeEnd(normalizedDay, this.fixedRange());

      this.commitSelection({ start: normalizedDay, end }, true);

      return;

    }



    const draftStart = this.rangeDraftStart();

    if (!draftStart) {

      this.rangeDraftStart.set(normalizedDay);

      this.dynamicMaxDate.set(

        toDay(computeDynamicMaxDate(normalizedDay, this.rangeConstraint()) ?? null)

      );

      this.selection.set({ start: normalizedDay });

      this.displayValue.set(formatDisplayValue({ start: normalizedDay }, this.displayFormat()));

      return;

    }



    const normalized = normalizeRangeSelection(draftStart, normalizedDay);

    this.rangeDraftStart.set(null);

    this.dynamicMaxDate.set(null);

    this.commitSelection(normalized, true);

  }



  protected onViewMonthChange(month: Dayjs): void {

    this.viewMonth.set(month.startOf('month'));

  }



  protected onTriggerKeydown(event: KeyboardEvent): void {
    if (this.isDisabled()) return;
    const combobox = this.comboboxRef();
    if (!combobox) return;

    // Permette di chiudere il popup da tastiera senza selezione.
    if (combobox.expanded() && event.key === 'Escape') {
      event.preventDefault();
      closeAriaCombobox(combobox);
      return;
    }

    if (combobox.expanded()) return;
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      combobox.open();
    }
  }

  private setupClickOutsideToClose(): void {
    if (!isPlatformBrowser(this.platformId) || typeof document === 'undefined') {
      return;
    }
    const controller = new AbortController();
    this.destroyRef.onDestroy(() => controller.abort());

    document.addEventListener(
      'pointerdown',
      (event: PointerEvent) => {
        const combobox = this.comboboxRef();
        if (!combobox?.expanded()) return;
        if (this.useMobileDrawer()) return;

        const target = event.target as Node | null;
        const container = this.host.nativeElement;
        if (!target || !container) return;

        // Click fuori dal campo+popup → chiudi
        if (!container.contains(target)) {
          closeAriaCombobox(combobox);
        }
      },
      { capture: true, signal: controller.signal }
    );
  }

  private updatePopupPlacement(): void {
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const input = document.getElementById(this.fieldId);
    const panel = this.popupPanel()?.nativeElement;
    if (!input) {
      return;
    }

    const rect = input.getBoundingClientRect();
    const panelHeight = panel?.offsetHeight ?? 340;
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;

    this.popupPlacement.set(
      spaceBelow < panelHeight && spaceAbove > spaceBelow ? 'above' : 'below'
    );
  }

  private applyInitialFocus(): void {
    if (!isPlatformBrowser(this.platformId) || typeof document === 'undefined') {
      this.pendingInitialFocus = false;
      return;
    }

    const panel = this.panelRefs()[0];
    if (!panel) {
      return;
    }

    const focusDate = this.resolveInitialFocusDate();
    if (focusDate && !focusDate.isSame(this.viewMonth(), 'month')) {
      this.viewMonth.set(focusDate.startOf('month'));
    }

    panel.focusInitialSelection(focusDate);
    this.pendingInitialFocus = false;
  }

  /** Giorno su cui portare il focus all'apertura (APG: data già selezionata, altrimenti oggi). */
  private resolveInitialFocusDate(): Dayjs | null {
    const selected = this.selection();
    if (selected?.start) {
      return selected.start.startOf('day');
    }
    const draftStart = this.rangeDraftStart();
    if (draftStart) {
      return draftStart.startOf('day');
    }
    return null;
  }



  private commitSelection(next: AriaDatePickerSelection, closePopup: boolean): void {

    this.selection.set(next);

    this.displayValue.set(formatDisplayValue(next, this.displayFormat()));

    if (!next.start.isSame(this.viewMonth(), 'month')) {

      this.viewMonth.set(next.start.startOf('month'));

    }

    this.onChange(selectionToFormValue(next, this.modelFormat(), this.isRangeLike()));

    this.onTouched();

    if (closePopup) {
      closeAriaCombobox(this.comboboxRef());
    }

  }



  writeValue(value: AriaDatePickerFormValue): void {

    this.syncingFromForm = true;

    const parsed = formValueToSelection(value, this.modelFormat());

    this.selection.set(parsed);

    this.rangeDraftStart.set(null);

    this.dynamicMaxDate.set(null);

    this.displayValue.set(formatDisplayValue(parsed, this.displayFormat()));

    if (parsed?.start) {

      this.viewMonth.set(parsed.start.startOf('month'));

    }

    this.syncingFromForm = false;

  }



  registerOnChange(fn: (value: AriaDatePickerFormValue) => void): void {

    this.onChange = fn;

  }



  registerOnTouched(fn: () => void): void {

    this.onTouched = fn;

  }



  setDisabledState(isDisabled: boolean): void {

    this.isDisabled.set(isDisabled);

  }

}

